// All tuning in one place. Units: 1 block = 1 world unit.

export const C = {
  world: {
    cols: 96,            // grid width (x), centered on 0
    rows: 240,           // dig depth (down from surface at y = 0)
    blockSize: 1,
  },

  orbit: {
    altitude: 150,       // mothership height above surface
    dockRange: 6,
    gravityFullBelow: 60,   // full planet gravity below this altitude
    gravityZeroAbove: 120,  // zero-g at/above this altitude (space)
  },

  mothership: {
    accel: 13,           // heavy but she gets going
    drag: 0.995,         // barely any natural braking (pow(drag, dt*60))
    maxSpeed: 20,
    hoverAltitude: 30,   // station-keeping floor above the terrain
    entryHeatBelow: 110, // descending below this = entry heat FX
  },

  bomb: {
    cooldown: 1.2,
    radius: 3.4,         // blast carve radius in blocks
    dropSpeed: -4,       // initial downward kick relative to ship
  },

  ship: {
    gravity: -22,
    thrust: 52,          // booster accel along the nose (W)
    retroThrust: 30,     // reverse burn opposite the nose (S)
    rotSpeed: 2.6,       // rad/sec attitude rotation (A/D) — full 360° allowed
    drag: 0.985,         // gentle overall damping (pow(drag, dt*60))
    maxFall: -34,
    maxRise: 30,
    maxSide: 26,
    safeLandSpeed: -9,   // faster than this = hard landing
    safeLandTilt: 0.45,  // more tilted than this on touchdown = hard landing
    hardLandEnergyCost: 12,
  },

  miner: {
    gravity: -30,
    jetpack: 58,         // upward accel while held
    move: 30,            // horizontal accel
    maxSpeed: 11,
    drag: 0.85,
    radius: 0.38,        // collision half-width
    height: 0.92,        // collision half-height
    laserRange: 7.5,
    laserDps: 3.2,       // block hp/sec at level 1
    magnetRange: 4.2,
    collectRange: 0.9,
    exitOffset: 2.2,     // spawn beside ship
    boardRange: 2.6,
  },

  energy: {
    max: 100,            // level 1
    jetpackDrain: 7,
    laserDrain: 9,
    ambientDrain: 0.55,  // just existing on-foot costs a little
    rechargeRate: 45,    // at ship
    emergencyOreLoss: 0.5,     // half the pouch spills at the failure point
    droneFlyTime: 2.6,         // seconds for the drone to reach you
    droneTowTime: 3.2,         // seconds being towed back to the ship
  },

  fuel: {
    max: 100,
    thrustDrain: 1.5,      // per second of active burn
    breakOrbitMin: 18,     // reserve needed to punch out of the gravity well
    solarRegen: 0.35,      // trickle so you're never hard-stranded
    costPerUnit: 1,        // credits per fuel unit at the dock
  },

  space: {
    bounds: { x: 620, y: 360 },   // open-space field half-extents
    asteroidCount: 46,
    cannonCooldown: 0.45,
    boltSpeed: 55,
    scrapPerRock: 3,       // credits for blasting a rock
    approachRange: 46,     // how close to a planet before you can dive
    planets: [             // fixed positions in open space (indexes PLANETS)
      { x: -420, y: 40 },
      { x: 80, y: -150 },
      { x: 460, y: 170 },
    ],
  },

  cargo: { max: 8 },     // level 1

  upgrades: {
    costs: [80, 240, 600, 1400],           // level 1→2 ... 4→5
    laserDpsPerLevel: 2.0,
    energyPerLevel: 60,
    cargoPerLevel: 6,
    thrustPerLevel: 10,
  },

  ore: {
    // value, block color, glow color
    copper: { value: 4, color: 0xc47f3a, glow: 0xff9950 },
    silver: { value: 10, color: 0xc9ced6, glow: 0xe8f0ff },
    gold: { value: 24, color: 0xf2c14e, glow: 0xffd970 },
    crystal: { value: 60, color: 0x54e8e0, glow: 0x8ffff8 },
    exotic: { value: 150, color: 0xc27ff2, glow: 0xe0a8ff },
  },

  camera: {
    z: 34,
    followLerp: 5.5,
    descentZ: 58,        // zoomed out while flying the ship
    shakeDecay: 6,
  },
};

export type OreKind = 'copper' | 'silver' | 'gold' | 'crystal' | 'exotic';

/** A destination world: palette + ore character. */
export interface PlanetDef {
  id: string;
  name: string;
  seed: number;
  skyLow: number;      // horizon color in atmosphere
  skyHigh: number;     // upper-sky tint before space
  soil: number;
  rocks: number[];
  deepRocks: number[];
  caveWall: number;
  spire: number;
  oreRichness: number; // multiplies ore roll chances
  hardness: number;    // multiplies block hp
  hazard: number;      // multiplies gas/lava chances
}

export const PLANETS: PlanetDef[] = [
  {
    id: 'ashfall', name: 'ASHFALL', seed: 1337,
    skyLow: 0x8fb7d9, skyHigh: 0x2c3e5e,
    soil: 0x8a6f4d,
    rocks: [0x5a5347, 0x655c4e, 0x4e4a42, 0x6b6152],
    deepRocks: [0x4a4550, 0x3f3b47, 0x554e5c],
    caveWall: 0x241f26, spire: 0x2e2836,
    oreRichness: 1, hardness: 1, hazard: 1,
  },
  {
    id: 'rustica', name: 'RUSTICA', seed: 5121,
    skyLow: 0xd9a06a, skyHigh: 0x6e3a2a,
    soil: 0x9a5c36,
    rocks: [0x7a4f38, 0x6e4630, 0x835a40, 0x5f3d2a],
    deepRocks: [0x54332a, 0x4a2d26, 0x613c30],
    caveWall: 0x2b1c16, spire: 0x4a2e22,
    oreRichness: 1.6, hardness: 1.4, hazard: 1.7,
  },
  {
    id: 'nyx', name: 'NYX', seed: 9313,
    skyLow: 0x7a6aa8, skyHigh: 0x1c1430,
    soil: 0x5a4a6e,
    rocks: [0x4a3f5e, 0x554870, 0x3f3752, 0x60527c],
    deepRocks: [0x322a44, 0x2a2338, 0x3c3150],
    caveWall: 0x161020, spire: 0x241c38,
    oreRichness: 2.4, hardness: 2.0, hazard: 2.2,
  },
];

/** 1 = full planet gravity near the surface, 0 = weightless in space. */
export function gravityScaleAt(y: number): number {
  const { gravityFullBelow, gravityZeroAbove } = C.orbit;
  if (y <= gravityFullBelow) return 1;
  if (y >= gravityZeroAbove) return 0;
  return 1 - (y - gravityFullBelow) / (gravityZeroAbove - gravityFullBelow);
}
